import React from 'react'
import styled, { css } from 'styled-components'

import type { IconProps } from '../icons'
import type { ClassNameProps } from '@root/shared/react'

export type SolidCardPalette =
  | 'purple'
  | 'cyan'
  | 'yellow'
  | 'blue'
  | 'orange'
  | 'pink'
  | 'amber'

export type SolidCardIconPlacement = 'inside'
export type SolidCardSize = 'm' | 's'

export type SolidCardProps = {
  Icon: React.FC<IconProps>
  size?: SolidCardSize
  color: SolidCardPalette
  title: string
  onClick?: () => void
} & ClassNameProps &
  (
    | {
        children: React.ReactNode
        value?: never
        subValue?: never
      }
    | {
        children?: never
        value: React.ReactNode
        subValue?: React.ReactNode
      }
  )

const IconSizeToNumber: Record<SolidCardSize, number> = {
  m: 18,
  s: 12,
}

export const SolidCard = (props: SolidCardProps) => {
  const { Icon, size: iconSize = 'm' } = props
  const iconEl = <Icon size={IconSizeToNumber[iconSize]} />

  return (
    <Root
      onClick={props.onClick}
      className={props.className}
      $palette={props.color}
    >
      <IconWrapper $iconSize={iconSize}>{iconEl}</IconWrapper>
      <Header>{props.title}</Header>
      <div></div>
      <Footer>
        {props.children ? (
          props.children
        ) : (
          <FooterContent>
            <Value>{props.value}</Value>
            <SubValue>{props.subValue}</SubValue>
          </FooterContent>
        )}
      </Footer>
    </Root>
  )
}

const Root = styled.div<{
  $palette: SolidCardPalette
  onClick?: () => void
}>`
  ${({ $palette, theme }) => {
    return css`
      --solid-color: ${theme.palette[$palette][9]};
      --bg-color: ${theme.palette[$palette][9]}33;
      --text-color: ${theme.palette[$palette][12]};
    `
  }}

  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'auto')};

  border-radius: 12px;
  background: var(--appearanceA-4, var(--appearanceA-4));
  display: grid;
  grid-template-columns: auto 1fr;

  padding: 10px 0 10px 10px;
  column-gap: 10px;
  align-items: center;
`

const IconSizeToWrapperSize: Record<SolidCardSize, number> = {
  m: 32,
  s: 20,
}
const IconSizeToWrapperPadding: Record<SolidCardSize, number> = {
  m: 6,
  s: 4,
}
const IconWrapper = styled.div<{ $iconSize: SolidCardSize }>`
  background: var(--solid-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${(x) => IconSizeToWrapperSize[x.$iconSize]}px;
  height: ${(x) => IconSizeToWrapperSize[x.$iconSize]}px;
  padding: ${(x) => IconSizeToWrapperPadding[x.$iconSize]}px;
`
const Header = styled.div`
  color: var(--appearance-inv-1);
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid var(--backgroundA-3, #ffffff12);
  display: flex;
  align-items: center;
  line-height: normal;

  padding: 7px 10px 7px 0;
`
const Footer = styled.div`
  padding: 7px 10px 0 0;
`
const FooterContent = styled.div`
  display: flex;
  column-gap: 6px;
  align-items: center;
  flex-wrap: wrap;
`

const Value = styled.div`
  color: var(--textA-12, rgba(255, 255, 255, 0.92));
  font-size: 16px;
  font-weight: 700;
  line-height: normal;
`
const SubValue = styled.div`
  font-size: 13px;
  font-weight: 600;
  line-height: 16px;
  color: var(--textA-9);
`

SolidCard.FooterContent = FooterContent
SolidCard.Value = Value
SolidCard.SubValue = SubValue
